import React, { useState, useEffect } from 'react';
import { emailService, delayedOrdersReportService } from '../services/api';
import { api } from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Mail, Send, CheckCircle2, XCircle, Settings, AlertTriangle, CalendarClock, Plus, Trash2, Package2 } from 'lucide-react';

export default function EmailSettingsPage() {
  const [config, setConfig] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  // Delayed orders report
  const [delayedSettings, setDelayedSettings] = useState(null);
  const [newRecipient, setNewRecipient] = useState('');
  const [savingDelayed, setSavingDelayed] = useState(false);
  const [sendingDelayed, setSendingDelayed] = useState(false);
  const [delayedPreview, setDelayedPreview] = useState(null);

  // Low raw stock alert
  const [lowStockSettings, setLowStockSettings] = useState(null);
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [lowStockNewRecipient, setLowStockNewRecipient] = useState('');
  const [savingLowStock, setSavingLowStock] = useState(false);
  const [sendingLowStock, setSendingLowStock] = useState(false);

  // Admin email for DDT discrepancies
  const [adminEmail, setAdminEmail] = useState('');
  const [savingAdminEmail, setSavingAdminEmail] = useState(false);

  useEffect(() => {
    loadConfig();
    loadDelayedSettings();
    loadLowStock();
    api.get('/raw-incoming-ddts/admin-email-settings').then(r => setAdminEmail(r.data?.administration_email || '')).catch(() => {});
  }, []);

  const saveAdminEmail = async () => {
    setSavingAdminEmail(true);
    try {
      await api.put('/raw-incoming-ddts/admin-email-settings', { administration_email: adminEmail.trim() });
      toast.success('Email amministrazione salvata');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally { setSavingAdminEmail(false); }
  };

  const loadLowStock = async () => {
    try {
      const [s, a] = await Promise.all([
        api.get('/raw-materials/low-stock-email/settings'),
        api.get('/raw-materials/threshold-alerts'),
      ]);
      setLowStockSettings(s.data);
      setLowStockAlerts(a.data || []);
    } catch { /* ignore */ }
  };

  const handleAddLowStockRecipient = () => {
    if (!lowStockSettings) return;
    const r = lowStockNewRecipient.trim();
    if (!r || !r.includes('@') || !r.includes('.')) return toast.error('Email non valida');
    if ((lowStockSettings.recipients || []).includes(r)) return toast.error('Già presente');
    setLowStockSettings(s => ({ ...s, recipients: [...(s.recipients || []), r] }));
    setLowStockNewRecipient('');
  };

  const handleRemoveLowStockRecipient = (r) => {
    setLowStockSettings(s => ({ ...s, recipients: s.recipients.filter(x => x !== r) }));
  };

  const handleSaveLowStock = async () => {
    setSavingLowStock(true);
    try {
      const res = await api.put('/raw-materials/low-stock-email/settings', {
        recipients: lowStockSettings.recipients,
        enabled: lowStockSettings.enabled,
        schedule_day_of_week: lowStockSettings.schedule_day_of_week,
        schedule_hour: lowStockSettings.schedule_hour,
        schedule_minute: lowStockSettings.schedule_minute,
      });
      setLowStockSettings(res.data);
      toast.success('Configurazione alert grezzi salvata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSavingLowStock(false);
    }
  };

  const handleSendLowStockNow = async () => {
    if (!lowStockSettings?.recipients?.length) return toast.error('Aggiungi almeno un destinatario');
    setSendingLowStock(true);
    try {
      const res = await api.post('/raw-materials/low-stock-email/send-now');
      const data = res.data;
      if ((data.failed || []).length === 0) {
        toast.success(`Email alert inviata a ${data.sent_to?.length || 0} destinatari (${data.alerts_count} prodotti sotto soglia)`);
      } else {
        toast.warning(`Inviato a ${data.sent_to?.length || 0}, falliti ${data.failed.length}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio');
    } finally {
      setSendingLowStock(false);
    }
  };

  const loadConfig = async () => {
    try {
      const res = await emailService.getConfig();
      setConfig(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const loadDelayedSettings = async () => {
    try {
      const [s, p] = await Promise.all([
        delayedOrdersReportService.getSettings(),
        delayedOrdersReportService.preview(),
      ]);
      setDelayedSettings(s.data);
      setDelayedPreview(p.data);
    } catch { /* ignore */ }
  };

  const handleAddRecipient = () => {
    if (!delayedSettings) return;
    const r = newRecipient.trim();
    if (!r || !r.includes('@') || !r.includes('.')) {
      toast.error('Inserisci un indirizzo email valido');
      return;
    }
    if ((delayedSettings.recipients || []).includes(r)) {
      toast.error('Indirizzo gia presente');
      return;
    }
    setDelayedSettings(s => ({ ...s, recipients: [...(s.recipients || []), r] }));
    setNewRecipient('');
  };

  const handleRemoveRecipient = (r) => {
    setDelayedSettings(s => ({ ...s, recipients: s.recipients.filter(x => x !== r) }));
  };

  const handleSaveDelayed = async () => {
    setSavingDelayed(true);
    try {
      const payload = {
        recipients: delayedSettings.recipients,
        enabled: delayedSettings.enabled,
        schedule_day_of_week: delayedSettings.schedule_day_of_week,
        schedule_hour: delayedSettings.schedule_hour,
        schedule_minute: delayedSettings.schedule_minute,
      };
      const res = await delayedOrdersReportService.updateSettings(payload);
      setDelayedSettings(res.data);
      toast.success('Configurazione report salvata');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSavingDelayed(false);
    }
  };

  const handleSendDelayedNow = async () => {
    if (!delayedSettings?.recipients?.length) {
      toast.error('Aggiungi almeno un destinatario prima di inviare');
      return;
    }
    setSendingDelayed(true);
    try {
      const res = await delayedOrdersReportService.sendNow();
      const data = res.data;
      const sentCount = data.sent_to?.length || 0;
      const failedCount = data.failed?.length || 0;
      if (failedCount === 0) {
        toast.success(`Report inviato a ${sentCount} destinatari`);
      } else {
        toast.warning(`Report inviato a ${sentCount} destinatari, ${failedCount} falliti`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio report');
    } finally {
      setSendingDelayed(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Inserisci un indirizzo email valido');
      return;
    }
    setSending(true);
    try {
      const res = await emailService.sendTest(testEmail);
      toast.success(res.data.message || 'Email di test inviata');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Errore invio email di test';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Caricamento...</div>;

  return (
    <div className="space-y-6" data-testid="email-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Impostazioni Email</h1>
        <p className="text-sm text-slate-500 mt-1">Configurazione del servizio di invio email transazionali</p>
      </div>

      {/* Config Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Stato Configurazione
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <span className="text-sm text-slate-600">Provider</span>
              <span className="text-sm font-medium">Resend</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <span className="text-sm text-slate-600">API Key</span>
              {config?.api_key_set ? (
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-100 text-emerald-800 gap-1"><CheckCircle2 className="w-3 h-3" />Configurata</Badge>
                  <span className="text-xs text-slate-400 font-mono">{config.api_key_prefix}</span>
                </div>
              ) : (
                <Badge className="bg-red-100 text-red-800 gap-1"><XCircle className="w-3 h-3" />Non configurata</Badge>
              )}
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-600">Email Mittente</span>
              <span className="text-sm font-mono">{config?.sender_email || '-'}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test Email */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Invio Email di Test
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-4">Invia un'email di test per verificare che la configurazione sia corretta.</p>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <Label className="text-xs uppercase tracking-wide text-slate-400">Destinatario</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="nome@azienda.it"
                className="mt-1"
                data-testid="test-email-input"
              />
            </div>
            <Button onClick={handleSendTest} disabled={sending || !config?.api_key_set} className="gap-2" data-testid="send-test-email-btn">
              <Send className="w-4 h-4" />
              {sending ? 'Invio...' : 'Invia Test'}
            </Button>
          </div>
          {!config?.api_key_set && (
            <p className="text-xs text-red-500 mt-2">API Key non configurata. Aggiungere RESEND_API_KEY nelle variabili d'ambiente.</p>
          )}
          {config?.sender_email === 'onboarding@resend.dev' && (
            <p className="text-xs text-amber-600 mt-2">
              Stai usando l'email mittente di default di Resend. In modalità test le email possono essere inviate solo all'indirizzo del proprietario dell'account.
              Per inviare a qualsiasi destinatario, verifica un dominio su resend.com/domains.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Delayed Orders Weekly Report */}
      {delayedSettings && (
        <Card data-testid="delayed-orders-report-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Report Settimanale Ordini in Ritardo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <p className="text-sm text-slate-500">
              Invio automatico via email del PDF "Vista Ordini per Prodotto" filtrato sui soli ordini in ritardo (residuo &gt; 0 e data consegna superata).
            </p>

            {/* Preview */}
            {delayedPreview && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-bold">Clienti</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{delayedPreview.total_clients}</p>
                </div>
                <div>
                  <p className="text-xs text-amber-700 uppercase tracking-wider font-bold">Righe in ritardo</p>
                  <p className="text-2xl font-bold text-amber-600 mt-1">{delayedPreview.total_lines}</p>
                </div>
                <div>
                  <p className="text-xs text-red-700 uppercase tracking-wider font-bold">Pezzi residui</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">{delayedPreview.total_remaining}</p>
                </div>
              </div>
            )}

            {/* Schedule */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div className="md:col-span-1">
                <Label className="text-xs uppercase tracking-wide text-slate-400">Abilitato</Label>
                <div className="h-10 flex items-center gap-2">
                  <Switch
                    checked={delayedSettings.enabled}
                    onCheckedChange={(v) => setDelayedSettings(s => ({ ...s, enabled: v }))}
                    data-testid="delayed-enabled-switch"
                  />
                  <span className="text-sm">{delayedSettings.enabled ? 'Si' : 'No'}</span>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-400">Giorno</Label>
                <Select
                  value={delayedSettings.schedule_day_of_week}
                  onValueChange={(v) => setDelayedSettings(s => ({ ...s, schedule_day_of_week: v }))}
                >
                  <SelectTrigger data-testid="delayed-day-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mon">Lunedi</SelectItem>
                    <SelectItem value="tue">Martedi</SelectItem>
                    <SelectItem value="wed">Mercoledi</SelectItem>
                    <SelectItem value="thu">Giovedi</SelectItem>
                    <SelectItem value="fri">Venerdi</SelectItem>
                    <SelectItem value="sat">Sabato</SelectItem>
                    <SelectItem value="sun">Domenica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-400">Ora</Label>
                <Input
                  type="number"
                  min="0"
                  max="23"
                  value={delayedSettings.schedule_hour}
                  onChange={(e) => setDelayedSettings(s => ({ ...s, schedule_hour: Number(e.target.value) }))}
                  data-testid="delayed-hour-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-400">Minuti</Label>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  value={delayedSettings.schedule_minute}
                  onChange={(e) => setDelayedSettings(s => ({ ...s, schedule_minute: Number(e.target.value) }))}
                  data-testid="delayed-minute-input"
                />
              </div>
            </div>
            <p className="text-xs text-slate-400 -mt-3 flex items-center gap-1">
              <CalendarClock className="w-3 h-3" />
              Fuso orario: Europe/Rome (CET/CEST)
            </p>

            {/* Recipients */}
            <div>
              <Label className="text-sm font-medium">Destinatari ({delayedSettings.recipients?.length || 0})</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  type="email"
                  value={newRecipient}
                  onChange={(e) => setNewRecipient(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddRecipient(); }}}
                  placeholder="commerciale@evotech.it"
                  data-testid="delayed-new-recipient-input"
                />
                <Button onClick={handleAddRecipient} variant="outline" data-testid="delayed-add-recipient-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Aggiungi
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 min-h-[28px]">
                {delayedSettings.recipients.length === 0 ? (
                  <span className="text-xs text-slate-400 italic">Nessun destinatario configurato</span>
                ) : delayedSettings.recipients.map((r) => (
                  <Badge key={r} className="bg-slate-100 text-slate-700 border border-slate-300 pl-3 pr-1 py-1 gap-1" data-testid={`delayed-recipient-${r}`}>
                    {r}
                    <button
                      type="button"
                      onClick={() => handleRemoveRecipient(r)}
                      className="hover:bg-red-100 hover:text-red-600 rounded p-0.5"
                      data-testid={`delayed-remove-${r}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <Button
                variant="outline"
                onClick={handleSendDelayedNow}
                disabled={sendingDelayed || !config?.api_key_set || !delayedSettings.recipients?.length}
                data-testid="delayed-send-now-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {sendingDelayed ? 'Invio...' : 'Invia ora'}
              </Button>
              <Button
                onClick={handleSaveDelayed}
                disabled={savingDelayed}
                data-testid="delayed-save-btn"
              >
                {savingDelayed ? 'Salvataggio...' : 'Salva configurazione'}
              </Button>
            </div>
            {!config?.api_key_set && (
              <p className="text-xs text-red-500">API Key Resend non configurata. Il report non puo essere inviato.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Email Automatiche Configurate</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3 py-2 border-b border-slate-100">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-800">Conferma Ordine</p>
                <p className="text-xs text-slate-400">Invio manuale dalla pagina dettaglio ordine con anteprima</p>
              </div>
            </div>
            <div className="flex items-start gap-3 py-2 border-b border-slate-100">
              <div className="w-2 h-2 bg-amber-500 rounded-full mt-1.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-800">Modifica Date di Consegna</p>
                <p className="text-xs text-slate-400">Invio automatico quando viene modificata la data consegna di una riga ordine</p>
              </div>
            </div>
            <div className="flex items-start gap-3 py-2">
              <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-800">Report Settimanale Ordini in Ritardo</p>
                <p className="text-xs text-slate-400">PDF con tutti gli ordini scaduti, inviato automaticamente ogni {delayedSettings ? `${{mon:'Lunedi',tue:'Martedi',wed:'Mercoledi',thu:'Giovedi',fri:'Venerdi',sat:'Sabato',sun:'Domenica'}[delayedSettings.schedule_day_of_week]} alle ${String(delayedSettings.schedule_hour).padStart(2,'0')}:${String(delayedSettings.schedule_minute).padStart(2,'0')}` : 'lunedi alle 08:00'} (Europe/Rome)</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Admin email for DDT discrepancies */}
      <Card data-testid="admin-email-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            Email Amministrazione — Differenze DDT Ingresso Grezzi
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600">
            Quando un DDT viene registrato con quantità dichiarata ≠ quantità effettivamente ricevuta, viene inviata automaticamente una notifica a questo indirizzo.
          </p>
          <div className="flex gap-2 items-end max-w-xl">
            <div className="flex-1">
              <Label>Indirizzo email amministrazione</Label>
              <Input type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)}
                placeholder="amministrazione@evotech.it" data-testid="admin-email-input" />
            </div>
            <Button onClick={saveAdminEmail} disabled={savingAdminEmail} data-testid="admin-email-save">
              {savingAdminEmail ? 'Salvataggio…' : 'Salva'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Low raw stock alert */}
      {lowStockSettings && (
      <Card data-testid="low-stock-email-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package2 className="w-5 h-5 text-orange-600" />
            Alert Settimanale Grezzi Sotto Soglia
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md bg-orange-50 border border-orange-200 p-3 text-sm text-orange-900">
            <div className="flex items-center justify-between">
              <span><b>{lowStockAlerts.length}</b> prodott{lowStockAlerts.length === 1 ? 'o' : 'i'} attualmente sotto soglia</span>
              <Button variant="outline" size="sm" onClick={loadLowStock}>Aggiorna</Button>
            </div>
            {lowStockAlerts.length > 0 && (
              <div className="mt-2 text-xs space-y-0.5 max-h-32 overflow-y-auto">
                {lowStockAlerts.slice(0, 6).map((a) => (
                  <div key={a.product_id} className="font-mono flex gap-2">
                    <span className="font-semibold">{a.raw_part_code}</span>
                    <span className="text-slate-600">· {a.product_code}</span>
                    <span className="text-slate-500">libero {a.libera} / soglia {a.min_stock_grezzo}</span>
                    <span className="text-red-700 font-semibold">-{a.deficit}</span>
                  </div>
                ))}
                {lowStockAlerts.length > 6 && <div className="text-slate-500">…e altri {lowStockAlerts.length - 6}</div>}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Attivo</Label>
              <div className="flex items-center gap-3">
                <Switch checked={!!lowStockSettings.enabled}
                  onCheckedChange={(v) => setLowStockSettings(s => ({ ...s, enabled: v }))}
                  data-testid="low-stock-enabled-switch" />
                <span className="text-sm text-slate-600">{lowStockSettings.enabled ? 'Job schedulato attivo' : 'Job disabilitato'}</span>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Programmazione</Label>
              <div className="flex items-center gap-2">
                <Select value={lowStockSettings.schedule_day_of_week}
                  onValueChange={(v) => setLowStockSettings(s => ({ ...s, schedule_day_of_week: v }))}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mon">Lunedì</SelectItem>
                    <SelectItem value="tue">Martedì</SelectItem>
                    <SelectItem value="wed">Mercoledì</SelectItem>
                    <SelectItem value="thu">Giovedì</SelectItem>
                    <SelectItem value="fri">Venerdì</SelectItem>
                    <SelectItem value="sat">Sabato</SelectItem>
                    <SelectItem value="sun">Domenica</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="number" min="0" max="23" className="w-16"
                  value={lowStockSettings.schedule_hour}
                  onChange={(e) => setLowStockSettings(s => ({ ...s, schedule_hour: parseInt(e.target.value || 0) }))} />
                <span>:</span>
                <Input type="number" min="0" max="59" className="w-16"
                  value={lowStockSettings.schedule_minute}
                  onChange={(e) => setLowStockSettings(s => ({ ...s, schedule_minute: parseInt(e.target.value || 0) }))} />
                <Badge variant="outline" className="text-[10px]">Europe/Rome</Badge>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium">Destinatari ({lowStockSettings.recipients?.length || 0})</Label>
            <div className="flex gap-2 mt-1">
              <Input type="email" placeholder="email@example.com"
                value={lowStockNewRecipient} onChange={(e) => setLowStockNewRecipient(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddLowStockRecipient())}
                data-testid="low-stock-new-recipient-input" />
              <Button type="button" onClick={handleAddLowStockRecipient} data-testid="low-stock-add-recipient-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {(lowStockSettings.recipients || []).length === 0 ? (
                <p className="text-xs text-slate-400">Nessun destinatario configurato</p>
              ) : lowStockSettings.recipients.map((r) => (
                <Badge key={r} variant="outline" className="text-xs px-2 py-1 gap-1">
                  {r}
                  <button type="button" onClick={() => handleRemoveLowStockRecipient(r)}
                    className="ml-1 hover:text-red-600" data-testid={`low-stock-remove-${r}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={handleSendLowStockNow}
              disabled={sendingLowStock || !lowStockSettings.recipients?.length}
              data-testid="low-stock-send-now-btn">
              <Send className="w-4 h-4 mr-2" />
              {sendingLowStock ? 'Invio…' : 'Invia ora'}
            </Button>
            <Button onClick={handleSaveLowStock} disabled={savingLowStock} data-testid="low-stock-save-btn">
              {savingLowStock ? 'Salvataggio…' : 'Salva configurazione'}
            </Button>
          </div>
        </CardContent>
      </Card>
      )}
    </div>
  );
}
